import firebase from 'firebase/app';
import { getFirebaseApp } from './firebase';
import config from '../config';
import { fromDateToTimeStamp } from '../common/dates';

const CHANNEL_COLLECTION = 'test';
const MAPPING_COLLECTION = 'mapping';
const PUBLISHED_COLLECTION = 'published';
const PREVIEW_COLLECTION = 'preview';
const CATEGORIES_COLLECTION = 'categories';
const BLOCKED_COLLECTION = 'blocked';
const CHAT_ACCESS_COLLECTION = 'single-chat-access';
const MULTICHANNEL_CONFIG_COLLECTION = 'multichannel-chat-config';

export const login = async (email, password) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp.auth().signInWithEmailAndPassword(email, password);
};

export const createRecaptchaVerifier = async (id) => {
  const app = await getFirebaseApp();
  return new firebase.auth.RecaptchaVerifier(id, { size: 'invisible' }, app);
};

export const getMFAVerificationId = async (resolver, appVerifier) => {
  const phoneInfoOptions = {
    multiFactorHint: resolver.hints.find(
      (hint) => hint.factorId === firebase.auth.PhoneMultiFactorGenerator.FACTOR_ID,
    ),
    session: resolver.session,
  };
  return new firebase.auth.PhoneAuthProvider().verifyPhoneNumber(phoneInfoOptions, appVerifier);
};

export const signInWithSMSCode = async (resolver, verificationId, verificationCode) => {
  const cred = firebase.auth.PhoneAuthProvider.credential(verificationId, verificationCode);
  const multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(cred);
  return resolver.resolveSignIn(multiFactorAssertion);
};

export const loginAnonymously = async (email, password) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp.auth().signInAnonymously();
};

export const logout = async () => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .auth()
    .signOut()
    .then(() => 'Success');
};

export const getCurrentUser = async () => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp.auth().currentUser;
};

export const getIdToken = async () => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp.auth().currentUser.getIdToken(true);
};

export const getIdTokenResult = async () => {
  const firebaseApp = await getFirebaseApp();
  if (firebaseApp.auth().currentUser) {
    return firebaseApp.auth().currentUser.getIdTokenResult();
  }
  return null;
};

export const onAuthStateChanged = async (callback) => {
  const firebaseApp = await getFirebaseApp();
  firebaseApp.auth().onAuthStateChanged(callback);
};

export const listenToChannelIds = async (onSnapshotCallback) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp.firestore().collection(MAPPING_COLLECTION).onSnapshot(onSnapshotCallback);
};

export const addMappingUrl = async (mappingId, newUrl) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(MAPPING_COLLECTION)
    .doc(mappingId)
    .update({
      urls: firebaseApp.firebase_.firestore.FieldValue.arrayUnion(newUrl),
    })
    .then((docRef) => {
      console.log('Mapping url added: ', docRef);
      return docRef;
    });
};

export const removeMappingUrl = async (mappingId, url) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(MAPPING_COLLECTION)
    .doc(mappingId)
    .update({
      urls: firebaseApp.firebase_.firestore.FieldValue.arrayRemove(url),
    })
    .then((docRef) => {
      console.log('Mapping url removed: ', docRef);
      return docRef;
    });
};

export const addMapping = async (channelId, newUrl) => {
  const firebaseApp = await getFirebaseApp();
  const mappingRef = firebaseApp.firestore().collection(MAPPING_COLLECTION).doc(channelId);

  const mapping = await mappingRef.get();
  if (!mapping.exists) {
    await mappingRef.set({ urls: [] });
  }

  return mappingRef
    .update({
      urls: firebaseApp.firebase_.firestore.FieldValue.arrayUnion(newUrl),
    })
    .then((docRef) => {
      console.log('Mapping url added: ', docRef);
      return docRef;
    });
};

export const fetchChannels = async () => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .get()
    .then((snapshot) => {
      return snapshot.docs.map((doc) => {
        return { id: doc.id, data: doc.data() };
      });
    });
};

export const listenToChannels = async (onSnapshotCallback, onSnapshotErrorCallback) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .onSnapshot(onSnapshotCallback, onSnapshotErrorCallback);
};

export const listenToChannelsWhere = async (
  showArchivedChannels = false,
  onSnapshotCallback,
  onSnapshotErrorCallback,
) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .where('archived', '==', showArchivedChannels)
    .onSnapshot(onSnapshotCallback, onSnapshotErrorCallback);
};

export const uploadImage = async (imageFile, progressCallback) => {
  const firebaseApp = await getFirebaseApp();
  const name = +new Date() + '-' + imageFile.name;
  const metadata = { contentType: imageFile.type };

  const task = firebaseApp
    .storage(firebaseConfig.storageBucket)
    .ref(`channels/${name}`)
    .put(imageFile, metadata);

  if (progressCallback) {
    task.on('state_changed', (snapshot) => progressCallback(snapshot));
  }

  return task.then((snapshot) => {
    return snapshot.ref.getDownloadURL();
  });
};

export const listenToChat = async (channelId, onSnapshotCallback) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .collection(PUBLISHED_COLLECTION)
    .orderBy('created', 'desc')
    .onSnapshot(onSnapshotCallback);
};

export const listenToChatPreview = async (channelId, onSnapshotCallback) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .collection(PREVIEW_COLLECTION)
    .orderBy('created', 'asc')
    .onSnapshot(onSnapshotCallback);
};

export const listenToChatPublished = async (channelId, onSnapshotCallback) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .collection(PUBLISHED_COLLECTION)
    .orderBy('created', 'asc')
    .onSnapshot(onSnapshotCallback);
};

export const fetchChannel = async (channelId) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .get()
    .then((doc) => {
      const channel = doc.data();
      return { ...channel, id: doc.id };
    });
};

export const listenToChannel = async (channelId, onSnapshotCallback) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .onSnapshot(onSnapshotCallback);
};

export const fetchThread = async (channelId) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .collection(PUBLISHED_COLLECTION)
    .get()
    .then((snapshot) => {
      return snapshot.docs.map((doc) => {
        return doc.data();
      });
    });
};

export const addComment = async (channelId, commentData) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .collection(PREVIEW_COLLECTION)
    .add(commentData)
    .then((docRef) => {
      return docRef;
    });
};

export const changeChannelStatus = async (channelId, live) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .update({ live: live });
};

const approveComment = async (channelId, commentId) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .collection(PREVIEW_COLLECTION)
    .doc(commentId)
    .update({ a: true });
};

const setCommentAnswered = async (channelId, commentId) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .collection(PREVIEW_COLLECTION)
    .doc(commentId)
    .update({ answered: true });
};

//TODO: generalize these methods...
export const ignoreComment = async (channelId, commentId) => {
  const firebaseApp = await getFirebaseApp();
  firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .collection(PREVIEW_COLLECTION)
    .doc(commentId)
    .update({ ignore: true });

  deletePublishedComment(channelId, commentId);
};

export const deleteComment = async (channelId, commentId) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .collection(PREVIEW_COLLECTION)
    .doc(commentId)
    .update({ d: true });
};

export const deleteCommentsFromTimestamp = async (channelId, timestamp) => {
  const firebaseApp = await getFirebaseApp();
  const batch = firebaseApp.firestore().batch();

  const allMessages = await firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .collection(PREVIEW_COLLECTION)
    .where('created', '<', fromDateToTimeStamp(timestamp))
    .get()
    .then((snapshot) => {
      return snapshot.docs.map((doc) => {
        return { id: doc.id, ...doc.data() };
      });
    });

  const docs = allMessages.map((m) => {
    return firebaseApp
      .firestore()
      .collection(CHANNEL_COLLECTION)
      .doc(channelId)
      .collection(PREVIEW_COLLECTION)
      .doc(m.id);
  });

  const res = await Promise.all(docs);
  res.forEach((ref) => batch.update(ref, { d: true }));

  await batch.commit();
};

export const revertComment = async (channelId, commentId) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .collection(PREVIEW_COLLECTION)
    .doc(commentId)
    .update({ d: false, a: false });
};

export const updateChannelProps = async (channelId, channelProps) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .update(channelProps)
    .then((docRef) => {
      return docRef;
    });
};

export const publishComment = async (channelId, commentData) => {
  await approveComment(channelId, commentData.commentId);
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .collection(PUBLISHED_COLLECTION)
    .doc(commentData.commentId)
    .set(commentData)
    .then((docRef) => {
      console.log('Published commentData written with ID: ', commentData.commentId);
      return docRef;
    });
};

export const setStickyComment = async (channelId, commentId, sticky) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .collection(PREVIEW_COLLECTION)
    .doc(commentId)
    .update({ sticky });
};

export const addAdminComment = async (user, channelId, content, userComment, isPublished) => {
  const firebaseApp = await getFirebaseApp();
  const data = {
    content,
    created: firebaseApp.serverTimestamp,
    userId: user.uid,
    userName: user.displayName,
    m: true, // is moderator comment
    a: true, // is visible in chat
    d: false, // is not deleted
  };
  // If answer to user comment
  if (userComment) {
    if (!isPublished) {
      await setCommentAnswered(channelId, userComment.commentId);
    }
    data.originalCommentId = userComment.commentId;
    data.originalContent = userComment.content;
    data.originalAuthor = userComment.userName;
    data.tmpId = userComment.tmpId;
  }
  let commentId = userComment?.commentId;
  if (!isPublished) {
    const docRef = await addPreviewComment(channelId, data);
    const doc = await docRef.get();
    commentId = doc.id;
  }
  await publishComment(channelId, { ...data, commentId: commentId });
};

export const updateAdminComment = async (channelId, commentId, content) => {
  const firebaseApp = await getFirebaseApp();
  const channel = await firebaseApp.firestore().collection(CHANNEL_COLLECTION).doc(channelId);
  await channel.collection(PREVIEW_COLLECTION).doc(commentId).update({ content });
  await channel.collection(PUBLISHED_COLLECTION).doc(commentId).update({ content });
};

const addPreviewComment = async (channelId, commentData) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .collection(PREVIEW_COLLECTION)
    .add(commentData)
    .then((docRef) => {
      return docRef;
    });
};

export const publishAdminAnswer = async (channelId, commentData) => {
  await setCommentAnswered(channelId, commentData.originalCommentId);
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .collection(PUBLISHED_COLLECTION)
    .add(commentData)
    .then((docRef) => {
      return docRef;
    });
};

export const deletePublishedComment = async (channelId, commentId) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .collection(PUBLISHED_COLLECTION)
    .where('commentId', '==', commentId)
    .get()
    .then(async (res) => {
      console.log('Published comment deleted', res.docs);
      if (res.docs.length) {
        const docRef = res.docs[0];
        await docRef.ref.delete();
        await revertComment(channelId, commentId);
        return res;
      } else {
        return 'Not found';
      }
    });
};

export const addChannel = async (channelData) => {
  console.log(channelData);
  const firebaseApp = await getFirebaseApp();
  return firebaseApp.firestore().collection(CHANNEL_COLLECTION).add(channelData);
};

export const setMultichannelChatConfig = async (multichannelId, chatId, data) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(MULTICHANNEL_CONFIG_COLLECTION)
    .doc(multichannelId)
    .collection('chats')
    .doc(chatId)
    .set(data, { merge: true });
};

export const updateMultichannelChatConfig = async (multichannelId, chatId, data) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(MULTICHANNEL_CONFIG_COLLECTION)
    .doc(multichannelId)
    .collection('chats')
    .doc(chatId)
    .update(data);
};

export const getMultichannelChatConfig = async (multichannelId, chatId) => {
  const firebaseApp = await getFirebaseApp();
  let snapShot = await firebaseApp
    .firestore()
    .collection(MULTICHANNEL_CONFIG_COLLECTION)
    .doc(multichannelId)
    .collection('chats')
    .doc(chatId)
    .get();

  if (snapShot.exists) {
    return snapShot.data();
  } else {
    return undefined;
  }
};

export const getMultichannelConfig = async () => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(MULTICHANNEL_CONFIG_COLLECTION)
    .get()
    .then((snapshot) => {
      return snapshot.docs.map((doc) => {
        return { id: doc.id, ...doc.data() };
      });
    });
};

export const createUser = async (displayName, firebaseApp) => {
  return firebaseApp
    .firestore()
    .collection(U_COLLECTION)
    .add({ displayName: displayName, dn: displayName.toLowerCase() });
};

export const listenToLiveChannels = async (onSnapshotCallback) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .where('live', '==', true)
    .onSnapshot(onSnapshotCallback);
};

export const getLiveChannels = async () => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .where('live', '==', true)
    .get()
    .then((snapshot) => {
      return snapshot.docs.map((doc) => {
        return { id: doc.id, data: doc.data() };
      });
    });
};

export const getCategories = async () => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CATEGORIES_COLLECTION)
    .get()
    .then((snapshot) => {
      return snapshot.docs.map((doc) => {
        return { id: doc.id, ...doc.data() };
      });
    });
};

export const listenToBlockedUsers = async (onSnapshotCallback) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp.firestore().collection(BLOCKED_COLLECTION).onSnapshot(onSnapshotCallback);
};

export const addBlockedUser = async (user) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp.firestore().collection(BLOCKED_COLLECTION).doc(user.id).set(user);
};

export const removeBlockedUser = async (userId) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp.firestore().collection(BLOCKED_COLLECTION).doc(userId).delete();
};

export const batchBlockComments = async (userId, channelId) => {
  const firebaseApp = await getFirebaseApp();
  const batch = firebaseApp.firestore().batch();

  const allMessages = await firebaseApp
    .firestore()
    .collection(CHANNEL_COLLECTION)
    .doc(channelId)
    .collection(PREVIEW_COLLECTION)
    .where('userId', '==', userId)
    .get()
    .then((snapshot) => {
      return snapshot.docs.map((doc) => {
        return { id: doc.id, ...doc.data() };
      });
    });

  const docs = allMessages.map((m) => {
    return firebaseApp
      .firestore()
      .collection(CHANNEL_COLLECTION)
      .doc(channelId)
      .collection(PREVIEW_COLLECTION)
      .doc(m.id);
  });

  const res = await Promise.all(docs);
  res.forEach((ref) => batch.update(ref, { blocked: true }));

  await batch.commit();
};

export const pushMessage = async (data) => {
  const token = await getIdToken();
  const res = await fetch(`${config.baseUrl}/push`, {
    method: 'post',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error('Failed to send push');
  }
};

export const getUserChatId = async (uid) => {
  const firebaseApp = await getFirebaseApp();

  let accessDoc = await firebaseApp.firestore().collection(CHAT_ACCESS_COLLECTION).doc(uid).get();

  if (accessDoc.exists) {
    let data = accessDoc.data();
    return data['chat-id'];
  } else {
    return undefined;
  }
};

export const setUserChatId = async (userId, chatId) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp
    .firestore()
    .collection(CHAT_ACCESS_COLLECTION)
    .doc(userId)
    .set({ 'chat-id': chatId }, { merge: true });
};

export const sendPasswordResetEmail = async (email) => {
  const firebaseApp = await getFirebaseApp();
  return firebaseApp.auth().sendPasswordResetEmail(email);
};
